import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import type { QuestionAnswer } from '@revelio/core';
import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import { openReplayStateLogger } from '../../hooks/open-replay';
import type { LLMTabContentForDispatch } from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import type {
  CustomDashAssistantEntityType,
  GlobalAssistantConversationTurn,
  GlobalAssistantEntityType,
} from './global-ai-assistant-api';
import type { LastAppliedFilterContext } from './global-ai-assistant-discovery-followup';
import type { GlobalAssistantResolvedRouteFilters } from './global-ai-assistant-route-filter-kind';
import type { PageGenProgressEvent } from './global-ai-page-gen-progress';
import { globalAssistantPathsMatch } from './global-assistant-route-filter-path';
import type {
  ChartDataDigest,
  FilterResolutionDigest,
} from './turn-outcome-digest';
import type { AssistantTaxonomyPayload } from './use-global-ai-assistant-taxonomy';

/**
 * Zustand queue for drawer -> page command flow.
 *
 * Problem this solves:
 * The drawer (global, outside CustomDash) needs to trigger work that only
 * makes sense inside CustomDash (dispatch, filter apply, stream gen). The
 * drawer typically emits RIGHT BEFORE `navigate()` — so the bridge that
 * should handle the command isn't mounted yet (old bridge is, for the
 * outgoing page). A synchronous "emit = run handler now" design loses
 * those commands.
 *
 * How this works:
 * - Commands are appended to a pending queue by `emit`.
 * - The bridge (inside CustomDash) subscribes via `useGlobalAiCommandStore`
 *   and drains the queue in a `useEffect`: for each command whose
 *   `entityType` matches the current page and (for route-filter commands)
 *   whose `targetPath` matches the current pathname, it consumes the
 *   command and runs its handler.
 * - Commands that don't match the current bridge stay in the queue until
 *   either a matching bridge mounts or they expire.
 * - `requestId` dedup prevents React StrictMode double-invoke from
 *   processing the same command twice.
 *
 * Alongside the queue the store also holds `activeTaxonomyByEntity` —
 * bridges publish their page's active taxonomy here so the drawer (which
 * has no filter context of its own) can use the page-level taxonomy.
 */

export const COMMAND_TTL_MS = 30_000;

export type GlobalAiCommand =
  | ({
      kind: 'applyRouteFilters';
      requestId: string;
      /**
       * Pre-resolved filters (`filterKind` + `response`). The drawer calls
       * the matching AI endpoint (TD v2 or company-screener) before emitting.
       */
      targetPath: string;
      entityType: GlobalAssistantEntityType;
      prompt?: string;
      merge?: boolean;
      /**
       * When true, the bridge forwards `pairRoleTaxonomyWithResponse: true`
       * to `applyAiSearchFilters`, so the destination page ADOPTS the
       * taxonomy the artifact's role ids were resolved under. Set ONLY by
       * the drawer's resolved-artifact link path (the user explicitly
       * clicked "show me this answer's filters", so the page taking the
       * answer's taxonomy is the correct outcome). All other emitters
       * (turn-runner `apply_filters`, page streams) leave it unset and the
       * apply stays unpaired — the historical page behavior.
       */
      pairTaxonomy?: boolean;
    } & GlobalAssistantResolvedRouteFilters)
  | {
      kind: 'applySingleChart';
      requestId: string;
      config: LLMTabContentForDispatch;
      filters?: TalentDiscoveryAiFilterSearchResponseV2;
      prompt?: string;
      entityType: CustomDashAssistantEntityType;
      targetPath: string;
    }
  | ({
      kind: 'startCustomPageStream';
      requestId: string;
      prompt: string;
      model?: string;
      entityType: CustomDashAssistantEntityType;
      answers?: QuestionAnswer[];
      /**
       * Bounded conversation context (recent turns + optional summary
       * of older turns). The bridge forwards this to the page-stream
       * backend so multi-turn intent and entity context survive
       * "Create custom page" / "Update current page" clicks.
       */
      conversation?: {
        turns: GlobalAssistantConversationTurn[];
        summary?: string;
      };
    } & (
      | {
          /**
           * Generates a fresh page layout from scratch and is cacheable.
           * The target path keeps the outgoing edit page from claiming work
           * intended for the newly opened draft.
           */
          mode?: 'create';
          targetPath: string;
        }
      | {
          /**
           * Patches the current page's layout in-place. The current tab
           * content IS the state to update, so there's no useful cache
           * key. Conversation context above carries pronoun and intent
           * history.
           */
          mode: 'update';
        }
    ));

export type GlobalAiCommandKind = GlobalAiCommand['kind'];

type QueuedCommand = {
  command: GlobalAiCommand;
  queuedAtMs: number;
  status: 'queued' | 'in_flight';
  /**
   * Pathname recorded by `claim()` when a bridge starts handling the
   * command. Used by `selectActiveCustomPageStreamForPath` to scope
   * update-mode streams (which have no `targetPath`) to the page that
   * actually owns the work, so navigating to a different custom dash
   * mid-stream doesn't surface a misleading loading state there.
   */
  claimedAtPath?: string;
};

/**
 * True when the queue contains a `startCustomPageStream` command (queued
 * or in flight) that is bound to `pathname`. Used by the CustomDash page
 * to mirror the chat's loading state on the page itself while the AI is
 * still building out the layout.
 *
 * - `mode: 'create'` commands carry an explicit `targetPath` — match on it.
 * - `mode: 'update'` commands have no target path; they're scoped via the
 *   `claimedAtPath` the owning bridge recorded when it called `claim()`.
 *   A queued (unclaimed) update only matches if no other bridge has
 *   claimed it yet AND the caller is on a CustomDash edit route — the
 *   bridge's own filter does that — so we only consider `in_flight`
 *   updates here and compare against `claimedAtPath`.
 */
export function selectActiveCustomPageStreamForPath(
  queue: readonly QueuedCommand[],
  pathname: string
): QueuedCommand | null {
  for (const entry of queue) {
    if (entry.command.kind !== 'startCustomPageStream') {
      continue;
    }
    const command = entry.command;
    if (command.mode === 'update') {
      if (
        entry.status === 'in_flight' &&
        entry.claimedAtPath !== undefined &&
        globalAssistantPathsMatch(pathname, entry.claimedAtPath)
      ) {
        return entry;
      }
      continue;
    }
    if (globalAssistantPathsMatch(pathname, command.targetPath)) {
      return entry;
    }
  }
  return null;
}

type GlobalAiCommandState = {
  /** Ordered FIFO of commands awaiting or mid-handling. Newest at tail. */
  queue: readonly QueuedCommand[];

  /**
   * True while any command is mid-flight. Drawer reads this to disable
   * submit buttons.
   */
  isCommandInFlight: boolean;

  /**
   * Current CustomDash page's taxonomy, published by the mounted bridge.
   * Keyed by entity type. Undefined for an entity means no bridge has
   * published for it in this session — the drawer falls back to saved
   * user preferences in that case.
   *
   * TODO(backend-adaptive): re-evaluate when the backend accepts
   * "adaptive". The primary consumer is `waitForTaxonomyChange` (used by
   * the drawer's refinement pass for adaptive-company). Once the
   * refinement goes away, the only remaining value is picking up session
   * overrides from the Preferences modal that haven't been "Set as
   * default" — niche; consider removing the whole
   * `activeTaxonomyByEntity` + `publishActiveTaxonomy` pair and letting
   * the drawer read saved prefs directly.
   */
  activeTaxonomyByEntity: Partial<
    Record<GlobalAssistantEntityType, AssistantTaxonomyPayload>
  >;

  /**
   * Last successfully applied global-assistant filter prompt per path.
   * Used to interpret filter refinements on the same page.
   */
  lastAppliedFilterContextByPath: Record<string, LastAppliedFilterContext>;

  /**
   * Outcome of the most recent global-assistant filter-apply, published by
   * the bridge once a `TalentDiscoveryAiFilterSearchResponseV2` has been
   * applied. The narration controller watches this (via the `at` timestamp)
   * to stream a grounded summary — including unsupported/unresolved filters
   * — into the latest assistant turn. Null until the first apply lands.
   */
  lastFilterApplyOutcome: {
    digest: FilterResolutionDigest;
    prompt: string;
    at: number;
  } | null;

  /**
   * Widget digests collected when a custom page finished generating,
   * published by the bridge's page-narration collector. The tool loop
   * watches this (via the `at` timestamp) to await the page's widget
   * digests before narrating the turn. Null until the first page
   * generation completes.
   */
  lastPageCollectionOutcome: {
    digests: ChartDataDigest[];
    prompt: string;
    /** How many widgets the collector was armed for (digests may be fewer). */
    expectedCount: number;
    at: number;
  } | null;

  /**
   * Set when a filter apply the chat already promised ("applying filters
   * now…") definitively failed — either the queued command aged out (TTL)
   * without any bridge consuming it (`reason: 'expired'`), or a bridge
   * consumed it and the apply threw (`reason: 'error'`). The drawer watches
   * this and surfaces the failure in-chat (DP4 optimistic apply_filters).
   * `paths` covers every apply that failed in the same event (one prune
   * pass can expire several commands at once).
   */
  lastFilterApplyFailure: {
    requestId: string;
    paths: string[];
    reason: 'expired' | 'error';
    at: number;
  } | null;

  /**
   * Bridge-side report of a CONSUMED apply command whose apply threw.
   * Complements pruneExpired's TTL detection so the in-chat failure surface
   * covers both ways an optimistic apply can die.
   */
  reportFilterApplyFailure: (input: {
    requestId: string;
    path: string;
  }) => void;

  /**
   * The chart titles a custom-page build is about to render, published by the
   * bridge as soon as the page-stream layout config has been dispatched (i.e.
   * before the widgets' data settles). The tool loop watches this (via `at`)
   * to stream a grounded "plan" narration line while the page builds. Null
   * until the first page generation dispatches a layout.
   */
  lastPagePlan: {
    titles: string[];
    entityType: GlobalAssistantEntityType;
    prompt: string;
    at: number;
  } | null;

  /**
   * Latest structured generation-progress event for an in-flight custom-page
   * build, published by the bridge as the page-stream emits. The tool loop
   * subscribes (via `seq`) and routes each to the progress channel. Null until
   * the first build publishes. `seq` is monotonic so the subscriber never
   * double-handles or misses an event.
   */
  lastPageGenProgress: {
    event: PageGenProgressEvent;
    seq: number;
    at: number;
  } | null;

  /**
   * Bridge calls this on mount and whenever its activeFilterMap changes.
   * Pass `undefined` for `taxonomy` on unmount to clear the published value.
   *
   * TODO(backend-adaptive): see `activeTaxonomyByEntity` above.
   */
  publishActiveTaxonomy: (
    entityType: GlobalAssistantEntityType,
    taxonomy: AssistantTaxonomyPayload | undefined
  ) => void;

  publishLastAppliedFilterContext: (
    targetPath: string,
    context: LastAppliedFilterContext
  ) => void;

  /** Publish the outcome of the latest filter-apply for narration. */
  setLastFilterApplyOutcome: (outcome: {
    digest: FilterResolutionDigest;
    prompt: string;
  }) => void;

  /** Publish the widget digests collected from the latest custom-page generation. */
  setLastPageCollectionOutcome: (outcome: {
    digests: ChartDataDigest[];
    prompt: string;
    expectedCount: number;
  }) => void;

  /** Publish the chart titles the latest custom-page build is about to render. */
  setLastPagePlan: (plan: {
    titles: string[];
    entityType: GlobalAssistantEntityType;
    prompt: string;
  }) => void;

  /** Publish a structured generation-progress event (monotonic `seq`). */
  publishPageGenProgress: (event: PageGenProgressEvent) => void;

  /** Append a command to the queue. Bridges consume it when they match. */
  emit: (command: GlobalAiCommand) => void;

  /**
   * Mark a queued command as `in_flight`. Returns true if the caller
   * should proceed (i.e. the command was previously 'queued' and this
   * caller is now the owner). Returns false if someone else already
   * claimed it (StrictMode double-invoke guard). The optional
   * `claimedAtPath` records which CustomDash route is handling the
   * command — used by `selectActiveCustomPageStreamForPath` to scope
   * page-local loading UI for update-mode streams.
   */
  claim: (requestId: string, claimedAtPath?: string) => boolean;

  /**
   * Mark a command done and remove it from the queue. Safe to call
   * whether the command was in-flight or still queued.
   */
  complete: (requestId: string) => void;

  /** Remove expired queued commands (> TTL old). Called by the bridge. */
  pruneExpired: () => void;
};

export const useGlobalAiCommandStore = create<GlobalAiCommandState>()(
  devtools(
    openReplayStateLogger(
      (set, get) => ({
        queue: [],
        isCommandInFlight: false,
        activeTaxonomyByEntity: {},
        lastAppliedFilterContextByPath: {},
        lastFilterApplyOutcome: null,
        lastPageCollectionOutcome: null,
        lastPagePlan: null,
        lastPageGenProgress: null,
        lastFilterApplyFailure: null,

        reportFilterApplyFailure: ({ requestId, path }) => {
          set(
            {
              lastFilterApplyFailure: {
                requestId,
                paths: [path],
                reason: 'error',
                at: Date.now(),
              },
            },
            false,
            'global-ai/reportFilterApplyFailure'
          );
        },

        publishActiveTaxonomy: (entityType, taxonomy) => {
          const current = get().activeTaxonomyByEntity;
          const existing = current[entityType];
          // Skip set if unchanged — avoids re-render churn when the bridge's
          // activeFilterMap updates with an unrelated filter change.
          if (taxonomy === undefined) {
            if (existing === undefined) return;
            const next = { ...current };
            delete next[entityType];
            set(
              { activeTaxonomyByEntity: next },
              false,
              `global-ai/clearTaxonomy:${entityType}`
            );
            return;
          }
          if (
            existing &&
            existing.rolesTax === taxonomy.rolesTax &&
            existing.skillsTax === taxonomy.skillsTax
          ) {
            return;
          }
          set(
            {
              activeTaxonomyByEntity: { ...current, [entityType]: taxonomy },
            },
            false,
            `global-ai/publishTaxonomy:${entityType}`
          );
        },

        publishLastAppliedFilterContext: (targetPath, context) => {
          const trimmedPrompt = context.prompt.trim();
          if (!trimmedPrompt) {
            return;
          }
          const current = get().lastAppliedFilterContextByPath;
          const existing = current[targetPath];
          if (
            existing &&
            existing.surfaceKind === context.surfaceKind &&
            existing.discoveryKind === context.discoveryKind &&
            existing.entityType === context.entityType &&
            existing.prompt === trimmedPrompt
          ) {
            return;
          }
          set(
            {
              lastAppliedFilterContextByPath: {
                ...current,
                [targetPath]: {
                  targetPath,
                  surfaceKind: context.surfaceKind,
                  ...(context.discoveryKind
                    ? { discoveryKind: context.discoveryKind }
                    : {}),
                  ...(context.entityType
                    ? { entityType: context.entityType }
                    : {}),
                  prompt: trimmedPrompt,
                },
              },
            },
            false,
            `global-ai/publishFilterContext:${targetPath}`
          );
        },

        setLastFilterApplyOutcome: (outcome) => {
          set(
            { lastFilterApplyOutcome: { ...outcome, at: Date.now() } },
            false,
            'global-ai/setLastFilterApplyOutcome'
          );
        },

        setLastPageCollectionOutcome: (outcome) => {
          set(
            { lastPageCollectionOutcome: { ...outcome, at: Date.now() } },
            false,
            'global-ai/setLastPageCollectionOutcome'
          );
        },

        setLastPagePlan: (plan) => {
          set(
            { lastPagePlan: { ...plan, at: Date.now() } },
            false,
            'global-ai/setLastPagePlan'
          );
        },

        publishPageGenProgress: (event) => {
          const seq = (get().lastPageGenProgress?.seq ?? 0) + 1;
          set(
            { lastPageGenProgress: { event, seq, at: Date.now() } },
            false,
            'global-ai/publishPageGenProgress'
          );
        },

        emit: (command) => {
          const state = get();
          // requestId dedupe — drop replays.
          if (
            state.queue.some((q) => q.command.requestId === command.requestId)
          ) {
            return;
          }
          set(
            {
              queue: [
                ...state.queue,
                { command, queuedAtMs: Date.now(), status: 'queued' },
              ],
            },
            false,
            `global-ai/emit:${command.kind}`
          );
        },

        claim: (requestId, claimedAtPath) => {
          const { queue } = get();
          const entry = queue.find((q) => q.command.requestId === requestId);
          if (!entry || entry.status !== 'queued') {
            return false;
          }
          set(
            {
              queue: queue.map((q) =>
                q.command.requestId === requestId
                  ? {
                      ...q,
                      status: 'in_flight',
                      ...(claimedAtPath !== undefined ? { claimedAtPath } : {}),
                    }
                  : q
              ),
              isCommandInFlight: true,
            },
            false,
            `global-ai/claim:${entry.command.kind}`
          );
          return true;
        },

        complete: (requestId) => {
          const { queue } = get();
          const next = queue.filter((q) => q.command.requestId !== requestId);
          set(
            {
              queue: next,
              isCommandInFlight: next.some((q) => q.status === 'in_flight'),
            },
            false,
            'global-ai/complete'
          );
        },

        pruneExpired: () => {
          const now = Date.now();
          const { queue } = get();
          const isFresh = (q: (typeof queue)[number]) =>
            q.status === 'in_flight' || now - q.queuedAtMs < COMMAND_TTL_MS;
          const next = queue.filter(isFresh);
          if (next.length === queue.length) {
            return;
          }
          // Expired filter-apply commands mean the user was told filters were
          // being applied but no page ever consumed them — record ALL of this
          // pass's expiries so the drawer can surface the failure in-chat
          // (DP4). Covers both apply shapes the chat promises: direct route
          // applies AND discovery search/applies (whose bridge may never
          // mount if the navigation was blocked). One prune pass can expire
          // several at once.
          const expiredPaths: string[] = [];
          let expiredRequestId: string | undefined;
          for (const q of queue) {
            if (!isFresh(q) && q.command.kind === 'applyRouteFilters') {
              expiredPaths.push(q.command.targetPath);
              expiredRequestId ??= q.command.requestId;
            }
          }
          set(
            {
              queue: next,
              ...(expiredRequestId !== undefined
                ? {
                    lastFilterApplyFailure: {
                      requestId: expiredRequestId,
                      paths: expiredPaths,
                      reason: 'expired' as const,
                      at: now,
                    },
                  }
                : {}),
            },
            false,
            'global-ai/pruneExpired'
          );
        },
      }),
      'global_ai_command'
    ),
    { name: 'GlobalAiCommand' }
  )
);

/**
 * TODO(backend-adaptive): DELETE along with the drawer's refinement pass
 * (see `navigateToMatch` in `global-ai-assistant-drawer.tsx`). No other
 * caller exists — this helper was added specifically to gate the
 * refinement on the initial apply landing.
 *
 * Wait for a specific command to leave the queue (bridge called `complete`).
 * Resolves to `true` on complete, `false` on timeout.
 */
export function waitForCommandComplete(
  requestId: string,
  timeoutMs: number,
  signal?: AbortSignal
): Promise<boolean> {
  const alreadyGone = !useGlobalAiCommandStore
    .getState()
    .queue.some((q) => q.command.requestId === requestId);
  if (alreadyGone) return Promise.resolve(true);
  if (signal?.aborted) return Promise.resolve(false);

  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      cleanup();
      resolve(false);
    }, timeoutMs);
    const onAbort = () => {
      cleanup();
      resolve(false);
    };
    const cleanup = () => {
      clearTimeout(timer);
      unsubscribe();
      signal?.removeEventListener('abort', onAbort);
    };
    signal?.addEventListener('abort', onAbort);
    const unsubscribe = useGlobalAiCommandStore.subscribe((state) => {
      const stillPresent = state.queue.some(
        (q) => q.command.requestId === requestId
      );
      if (!stillPresent) {
        cleanup();
        resolve(true);
      }
    });
  });
}

/**
 * TODO(backend-adaptive): DELETE along with the drawer's refinement pass
 * (see `navigateToMatch` in `global-ai-assistant-drawer.tsx`). No other
 * caller exists — this helper was added specifically to detect
 * adaptive-taxonomy re-resolution after an `applyRouteFilters` command
 * lands. When the backend accepts `"adaptive"` directly, there's no
 * longer a pre-apply / post-apply taxonomy discrepancy to watch for.
 */
export function waitForTaxonomyChange(
  entityType: GlobalAssistantEntityType,
  baseline: AssistantTaxonomyPayload,
  timeoutMs: number
): Promise<AssistantTaxonomyPayload | null> {
  const current =
    useGlobalAiCommandStore.getState().activeTaxonomyByEntity[entityType];
  if (
    current &&
    (current.rolesTax !== baseline.rolesTax ||
      current.skillsTax !== baseline.skillsTax)
  ) {
    return Promise.resolve(current);
  }
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      unsubscribe();
      resolve(null);
    }, timeoutMs);
    const unsubscribe = useGlobalAiCommandStore.subscribe((state) => {
      const next = state.activeTaxonomyByEntity[entityType];
      if (
        next &&
        (next.rolesTax !== baseline.rolesTax ||
          next.skillsTax !== baseline.skillsTax)
      ) {
        clearTimeout(timer);
        unsubscribe();
        resolve(next);
      }
    });
  });
}
